import { format } from 'date-fns'

// Modified from current _modelLogic.js
export default function PTGMLogic(isModelInSeason, hourlyData, state) {
  const {
    startDate,
    firstSpray,
    secondSpray,
    thirdSpray,
    pollenTubeModel,
  } = state

  let startDateIdx = 0
  let firstSprayIdx = startDateIdx + 1
  let secondSprayIdx = firstSprayIdx + 1
  let thirdSprayIdx = secondSprayIdx + 1

  if (startDate) {
    const idxSD = hourlyData
      .slice(startDateIdx)
      .findIndex(
        (d) => parseInt(d.ms / 1000) === parseInt(startDate.getTime() / 1000),
      )
    if (idxSD !== -1) startDateIdx = idxSD
  }

  if (firstSpray) {
    if (firstSpray && typeof firstSpray.getTime === 'function') {
      const idx1 = hourlyData
        .slice(startDateIdx)
        .findIndex(
          (d) => parseInt(d.ms / 1000) === parseInt(firstSpray.getTime() / 1000),
        )
      if (idx1 !== -1) firstSprayIdx = idx1
    }
  }

  if (secondSpray) {
    const idx2 = hourlyData
      .slice(startDateIdx)
      .findIndex(
        (d) => parseInt(d.ms / 1000) === parseInt(secondSpray.getTime() / 1000),
      )
    if (idx2 !== -1) secondSprayIdx = idx2
  }

  if (thirdSpray) {
    const idx3 = hourlyData
      .slice(startDateIdx)
      .findIndex(
        (d) => parseInt(d.ms / 1000) === parseInt(thirdSpray.getTime() / 1000),
      )
    if (idx3 !== -1) thirdSprayIdx = idx3
  }

  let mData = null
  if (isModelInSeason) {
    let gdh = 0 // Cumulative Growing Degree Hours for initial growth
    let firstSprayGdh = 0
    let secondSprayGdh = 0
    let thirdSprayGdh = 0

    // Temperature statistics multivariate versions
    let tempSum = 0, tempCount = 0, tempLow = Infinity, tempHigh = -Infinity
    let firstSprayTempSum = 0, firstSprayTempCount = 0, firstSprayTempLow = Infinity, firstSprayTempHigh = -Infinity
    let secondSprayTempSum = 0, secondSprayTempCount = 0, secondSprayTempLow = Infinity, secondSprayTempHigh = -Infinity
    let thirdSprayTempSum = 0, thirdSprayTempCount = 0, thirdSprayTempLow = Infinity, thirdSprayTempHigh = -Infinity

    mData = hourlyData.slice(startDateIdx).map((d, i) => {
      if (d.temp === 'M') {
        return {
          ...d,
          temp: 'N/A',
          gdh: 'N/A',
          percentStyletPenetration: 'N/A',
          upperBound: 'N/A',
          lowerBound: 'N/A',
        }
      } else {
        // Calculate Growing Degree Hours
        const baseTemp = 39.2
        const temp = Math.round(d.temp)
        const hourlyGdh = temp > baseTemp ? temp - baseTemp : 0

        // Default calculation starting from the start date
        gdh += hourlyGdh
        let activeGdh = gdh

        tempSum += temp
        tempCount += 1
        tempLow = Math.min(tempLow, temp)
        tempHigh = Math.max(tempHigh, temp)
        let activeTime = tempCount
        let activeAvgTemp = tempSum / tempCount
        let activeTempLow = tempLow
        let activeTempHigh = tempHigh

        // Reset calculations at spray dates
        if (firstSpray && i >= firstSprayIdx) {
          if (i === firstSprayIdx) {
            firstSprayGdh = 0
            firstSprayTempSum = 0
            firstSprayTempCount = 0
            firstSprayTempLow = Infinity
            firstSprayTempHigh = -Infinity
          }
          firstSprayGdh += hourlyGdh
          firstSprayTempSum += temp
          firstSprayTempCount += 1
          firstSprayTempLow = Math.min(firstSprayTempLow, temp)
          firstSprayTempHigh = Math.max(firstSprayTempHigh, temp)

          activeGdh = firstSprayGdh
          activeTime = firstSprayTempCount
          activeAvgTemp = firstSprayTempSum / firstSprayTempCount
          activeTempLow = firstSprayTempLow
          activeTempHigh = firstSprayTempHigh
        }

        if (secondSpray && i >= secondSprayIdx) {
          if (i === secondSprayIdx) {
            secondSprayGdh = 0
            secondSprayTempSum = 0
            secondSprayTempCount = 0
            secondSprayTempLow = Infinity
            secondSprayTempHigh = -Infinity
          }
          secondSprayGdh += hourlyGdh
          secondSprayTempSum += temp
          secondSprayTempCount += 1
          secondSprayTempLow = Math.min(secondSprayTempLow, temp)
          secondSprayTempHigh = Math.max(secondSprayTempHigh, temp)

          activeGdh = secondSprayGdh
          activeTime = secondSprayTempCount
          activeAvgTemp = secondSprayTempSum / secondSprayTempCount
          activeTempLow = secondSprayTempLow
          activeTempHigh = secondSprayTempHigh
        }

        if (thirdSpray && i >= thirdSprayIdx) {
          if (i === thirdSprayIdx) {
            thirdSprayGdh = 0
            thirdSprayTempSum = 0
            thirdSprayTempCount = 0
            thirdSprayTempLow = Infinity
            thirdSprayTempHigh = -Infinity
          }
          thirdSprayGdh += hourlyGdh
          thirdSprayTempSum += temp
          thirdSprayTempCount += 1
          thirdSprayTempLow = Math.min(thirdSprayTempLow, temp)
          thirdSprayTempHigh = Math.max(thirdSprayTempHigh, temp)

          activeGdh = thirdSprayGdh
          activeTime = thirdSprayTempCount
          activeAvgTemp = thirdSprayTempSum / thirdSprayTempCount
          activeTempLow = thirdSprayTempLow
          activeTempHigh = thirdSprayTempHigh
        }

        const isAtSprayPoint = i === 0
          || (firstSpray && i === firstSprayIdx)
          || (secondSpray && i === secondSprayIdx)
          || (thirdSpray && i === thirdSprayIdx);

        // Calculate Percent Stylet Penetration based on selected model
        let percentStyletPenetration = 0;

        if (isAtSprayPoint) {
          percentStyletPenetration = 0;
        } else {
          switch (pollenTubeModel.value) {
            case 'linear-univariate':
              percentStyletPenetration = 100 * (0.11 + (0.0011 * activeGdh));
              break;
            case 'logistic-univariate':
              percentStyletPenetration = -6.93 + (0.02 * activeGdh);
              percentStyletPenetration = Math.max(0, percentStyletPenetration); // Not much use in displaying negative
              break;
            case 'linear-multivariate':
              // PSP = -0.26 + 0.0003(GDH) + 0.005(TIME) + 0.05(AVE.TEMP) - 0.03(TEMPL) + 0.003(TEMPH)
              percentStyletPenetration = 100 * (
                -0.26 +
                0.0003 * activeGdh +
                0.005 * activeTime +
                0.05 * activeAvgTemp -
                0.03 * activeTempLow +
                0.003 * activeTempHigh
              );
              percentStyletPenetration = Math.max(0, percentStyletPenetration); // Not much use in displaying negative
              break;
            case 'logistic-multivariate':
              // Log Odds Fertilization = -7.62 + 0.01(GDH) + 0.02(TIME) + 0.38(AVE.TEMP) - 0.19(TEMPL) - 0.16(TEMPH)
              percentStyletPenetration =
                -0.26 +
                0.01 * activeGdh +
                0.02 * activeTime +
                0.38 * activeAvgTemp -
                0.19 * activeTempLow -
                0.16 * activeTempHigh;
              percentStyletPenetration = Math.max(0, percentStyletPenetration); // Not much use in displaying negative
              break;

            default:
              // Fallback to linear-univariate
              percentStyletPenetration = 100 * (0.11 + (0.0011 * activeGdh));
          }

        }

        const upperBound = percentStyletPenetration + 17;
        const lowerBound = percentStyletPenetration - 45;

        const datapoint = {
          ...d,
          tempCount,
          growingDegreeHours: Math.round(activeGdh),
          percentStyletPenetration,
          upperBound: upperBound,
          lowerBound: Math.max(0, lowerBound),
        };

        console.log(datapoint);
        return datapoint;
      }
    })
  }
  // Data to display in Results Table
  let tableData = null
  if (isModelInSeason) tableData = mData.slice().reverse()

  // Data in CSV format to export
  let csvData = null
  if (isModelInSeason) {
    csvData = mData.map((h) => {
      if (h.temp !== 'N/A') {
        return {
          Date: format(h.date, 'yyyy-MM-dd HH:00'),
          'Air Temp (F)': Math.round(h.temp),
          'Growing Degree Hours': h.gdh,
          'Percent Stylet Penetration': h.percentStyletPenetration,
          'Upper Bound': h.upperBound,
          'LowerBound': h.lowerBound,
        }
      }
      return {
        Date: format(h.date, 'yyyy-MM-dd HH:00'),
        'Air Temp (F)': h.temp,
        'Growing Degree Hours': 'N/A',
        'Percent Stylet Penetration': 'N/A',
        'Upper Bound': 'N/A',
        'LowerBound': 'N/A',
      }
    })
  }

  let graphData = null
  const dataLength = mData ? mData.length : 0
  const currentHour = new Date().getHours()
  const includedHours = [0, 4, 8, 12, 16, 20, currentHour]

  if (isModelInSeason) {
    if (dataLength < 1000) {
      graphData = mData.map((d) => {
        return {
          date: d.date,
          ms: d.ms,
          tempCount: d.tempCount,
          formattedDate: format(d.date, 'MM-dd HH:00'),
          percentStyletPenetration: d.percentStyletPenetration,
          upperBound: d.upperBound,
          lowerBound: d.lowerBound,
          temp: d.temp,
        }
      })
    } else {
      graphData = mData
        .map((d, i) => {
          const hour = d.date.getHours()
          if (
            includedHours.includes(hour) ||
            i === startDateIdx ||
            i === firstSprayIdx ||
            i === secondSprayIdx ||
            i === thirdSprayIdx
          ) {
            return {
              date: d.date,
              ms: d.ms,
              tempCount: d.tempCount,
              formattedDate: format(d.date, 'MM-dd HH:00'),
              percentStyletPenetration: d.percentStyletPenetration,
              upperBound: d.upperBound,
              lowerBound: d.lowerBound,
              temp: d.temp,
            }
          }
          return null
        })
        .filter(Boolean)
    }
  }
  return {
    mData,
    tableData,
    csvData,
    graphData,
  }
}
